import { EnvironmentProviders, inject, provideAppInitializer as provideNgAppInitializer } from '@angular/core';

import { filter, lastValueFrom, switchMap, tap } from 'rxjs';

import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';

/**
 * MSAL Browser 4+ throws `uninitialized_public_client_application` on any instance call
 * made before `initialize()` resolves, so app bootstrap must wait for it. The redirect login
 * result is processed here as well, before routing starts, so route guards already see the
 * signed-in account on the first navigation after returning from Microsoft login.
 */
export const provideAppInitializer = (): EnvironmentProviders =>
	provideNgAppInitializer(() => {
		const msalService = inject(MsalService);
		const msalBroadcastService = inject(MsalBroadcastService);

		return lastValueFrom(
			msalService.initialize().pipe(
				switchMap(() => msalService.handleRedirectObservable()),
				tap(() => {
					msalBroadcastService.inProgress$
						.pipe(
							filter((status: InteractionStatus) => status === InteractionStatus.None),
							tap(() => {
								const activeAccount = msalService.instance.getActiveAccount();
								const allAccounts = msalService.instance.getAllAccounts();

								if (!activeAccount && allAccounts.length > 0) {
									const [newActiveAccount] = allAccounts;

									msalService.instance.setActiveAccount(newActiveAccount);
								}
							}),
						)
						.subscribe();
				}),
			),
		);
	});
